import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authApi, { PlanResponse } from '../../services/authApi';
import './PricingPage.css';

const FAQ = [
  {
    q: 'Will I get access to all future learning?',
    a: 'Yes. Pro subscribers get access to all learning we publish while their subscription is active — including new ones added after you signed up.',
  },
  {
    q: 'Is this a SEBI registered service?',
    a: 'No. SwingRadarX Academy is an educational platform only. We are not a SEBI registered investment advisor. All content is for learning purposes — not investment advice.',
  },
  {
    q: 'Can I cancel any time?',
    a: 'Monthly plans can be cancelled at any time — your access continues until the end of the billing period. Annual plans are non-refundable after 7 days.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'UPI, net banking, credit/debit cards (Visa, Mastercard, RuPay) via Razorpay. All payments are processed securely.',
  },
];

declare global {
  interface Window {
    Razorpay: any;
  }
}

const loadRazorpay = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PricingPage: React.FC = () => {
  const { user, isAuthenticated, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [payingPlanId, setPayingPlanId] = useState<number | null>(null);

  // Redirect premium users away — they have nothing to buy here
  useEffect(() => {
    if (user?.isPremium) navigate('/courses', { replace: true });
  }, [user?.isPremium, navigate]);

  useEffect(() => {
    authApi.getPlans('COMMUNITY').then((res) => {
      setPlans(res.data || []);
    }).catch(() => {
      // silently fallback to empty — Free tier still shown
    }).finally(() => setLoadingPlans(false));
  }, []);

  const openLoginModal = () => {
    window.dispatchEvent(new CustomEvent('open-login-modal'));
  };

  const handlePay = async (plan: PlanResponse) => {
    if (!isAuthenticated) { openLoginModal(); return; }
    setPayingPlanId(plan.id);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { alert('Failed to load payment gateway. Please try again.'); return; }

      const orderRes = await authApi.createPaymentOrder({ planId: plan.id });
      const order = orderRes.data;

      const rzpOptions = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        name: 'SwingRadarX Academy',
        description: plan.planName,
        order_id: order.orderId,
        prefill: {
          name: order.userName,
          email: order.userEmail,
          contact: order.userContact,
        },
        theme: { color: '#3B82F6' },
        handler: async (paymentResponse: any) => {
          try {
            await authApi.verifyPayment({
              razorpayOrderId: paymentResponse.razorpay_order_id,
              razorpayPaymentId: paymentResponse.razorpay_payment_id,
              razorpaySignature: paymentResponse.razorpay_signature,
            });
            await refreshUserProfile();
            alert(`Payment successful! You now have access to ${plan.planName}.`);
          } catch {
            alert('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => setPayingPlanId(null),
        },
      };

      const rzp = new window.Razorpay(rzpOptions);
      rzp.on('payment.failed', () => {
        alert('Payment failed. Please try again.');
        setPayingPlanId(null);
      });
      rzp.open();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Could not create payment order. Please try again.');
    } finally {
      setPayingPlanId(null);
    }
  };

  const periodLabel = (months: number) => {
    if (months === 1)  return '/month';
    if (months === 3)  return '/3 months';
    if (months === 6)  return '/6 months';
    if (months === 12) return '/year';
    return `/${months} months`;
  };

  return (
    <div className="pp-wrap">
      <div className="pp-inner">
        <div className="pp-header">
          <p className="pp-eyebrow">Pricing</p>
          <h1 className="pp-title">Transparent pricing. No hidden fees.</h1>
          <p className="pp-sub">All prices are in INR. Pro plans give full access — learning, screeners, and the community forum.</p>
        </div>

        <div className="pp-grid">
          {/* Plans from DB */}
          {loadingPlans ? (
            <div className="pp-card pp-card-loading">
              <div className="pp-loading-dot" /><div className="pp-loading-dot" /><div className="pp-loading-dot" />
            </div>
          ) : (() => {
            const maxPrice = Math.max(...plans.map(p => p.price));
            return plans.map((plan) => {
              const isFeatured = plan.price === maxPrice;
              const isPaying = payingPlanId === plan.id;
              return (
                <div key={plan.id} className={`pp-card ${
                  isFeatured          ? 'pp-card--featured' :
                  plan.durationMonths === 6 ? 'highlighted-purple' :
                                        'highlighted'
                }`}>
                  {isFeatured && <div className="pp-badge">⭐ Best Value</div>}
                  <div className="pp-plan-name">{plan.planName}</div>
                  <div className="pp-price">
                    ₹{plan.price.toLocaleString('en-IN')}
                    <span className="pp-period">{periodLabel(plan.durationMonths)}</span>
                  </div>
                  <div className="pp-gst-label">GST included</div>
                  {plan.originalPrice > plan.price && (
                    <div className="pp-savings-row">
                      <span className="pp-original-price">₹{plan.originalPrice.toLocaleString('en-IN')}</span>
                      <span className="pp-discount-badge">{plan.discountPercent}% OFF</span>
                    </div>
                  )}
                  <div className="pp-divider" />
                  <ul className="pp-features">
                    {(plan.features || []).map((f) => (
                      <li key={f} className="pp-feature pp-feature-yes"><CheckIcon />{f.trim()}</li>
                    ))}
                  </ul>
                  <button
                    className={`pp-cta ${
                      isFeatured                 ? 'pp-cta-featured' :
                      plan.durationMonths === 6  ? 'pp-cta-purple' :
                                                   'pp-cta-primary'
                    }`}
                    onClick={() => handlePay(plan)}
                    disabled={isPaying}
                  >
                    {isPaying ? 'Processing...' : `Start ${plan.planName}`}
                  </button>
                </div>
              );
            });
          })()}
        </div>

        <div className="pp-disclaimer">
          <strong>SEBI Disclaimer:</strong> SwingRadarX Academy is an educational platform and is not a SEBI registered investment advisor, research analyst, or broker.
          Subscription fees are for access to educational content only — not for investment advice or trade recommendations.
          All investments in securities market are subject to market risk. Read all related documents carefully before investing.
        </div>

        <div className="pp-faq-section">
          <h2 className="pp-faq-title">Frequently Asked Questions</h2>
          <div className="pp-faq-grid">
            {FAQ.map((item) => (
              <div key={item.q} className="pp-faq-item">
                <div className="pp-faq-q">{item.q}</div>
                <div className="pp-faq-a">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
